import React, { useState, useEffect, useRef } from "react";
import useQRScanner from "../utils/useQrCode";
import { WhiteBoxWrapper, VideoScanBar, VideoWrapper } from "./Login.style";
import { Alert, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SectionHeader from "./HeaderSection";
import { v4 as uuidv4 } from "uuid";
import { fetchAPI } from "../utils/fetchAPI";

const AddDeviceForm: React.FC = () => {
  const [name, setName] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [returnable, setReturnable] = useState<boolean>(false);
  const [qrCodeForm, setQrCodeForm] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [avatar, setAvatar] = useState<{ blob: Blob | null; filename: string | null }>({ blob: null, filename: null });
  const [capturing, setCapturing] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [captureMessage, setCaptureMessage] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const videoRefCamera = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { videoRef, qrCode, setQrCode, error: cameraError } = useQRScanner();
  const navigate = useNavigate();

  useEffect(() => {
    const sendQRCode = async () => {
      if (!qrCode) return;
      setIsLoading(true);
      setErrorMessage("");

      const data = await fetchAPI("POST", "checkqr.php", { qr: qrCode });

      if (data && data.success) {
        setQrCodeForm("");
        setErrorMessage("Kod QR już jest wykorzystany");
      } else {
        setQrCodeForm(qrCode);
      }

      setIsLoading(false);
    };

    sendQRCode();
  }, [qrCode, setQrCode]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const uniqueFilename = `${uuidv4()}-${file.name}`;
      setAvatar({ blob: file, filename: uniqueFilename });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleCapture = () => {
    setCaptureLoading(true);
    if (canvasRef.current && videoRefCamera.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        context.drawImage(videoRefCamera.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
        canvasRef.current.toBlob((blob) => {
          if (blob) {
            const uniqueFilename = `${uuidv4()}.png`;
            const imageUrl = URL.createObjectURL(blob);
            setPreviewImage(imageUrl);
            setAvatar({ blob, filename: uniqueFilename });
            setCaptureMessage("Zdjęcie zostało zapisane jako avatar.");
          }
          setCaptureLoading(false);
        }, "image/png");
      }
    }
    setCapturing(false);
  };

  const handleSendData = async () => {
    if (!name || !description || !qrCodeForm) {
      alert("Wypełnij wszystkie pola i zeskanuj kod QR.");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("category", category);
    formData.append("type", type);
    formData.append("description", description);
    formData.append("returnable", String(returnable));
    formData.append("qrCodeForm", qrCodeForm);

    if (avatar.blob && avatar.filename) {
      formData.append("avatar", avatar.blob, avatar.filename);
    }

    try {
      setIsLoading(true);
      await fetchAPI("POST", "adddevice.php", formData);

      navigate("/devices");
      setName("");
      setDescription("");
      setReturnable(false);
      setQrCodeForm("");
      setAvatar({ blob: null, filename: null });
      setPreviewImage(null);
    } catch (error) {
      console.error("Błąd podczas wysyłania danych:", error);
      alert("Nie udało się wysłać danych. Spróbuj ponownie.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (capturing && videoRefCamera.current) {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: { exact: "environment" } } })
        .then((stream) => {
          videoRefCamera.current!.srcObject = stream;
          videoRefCamera.current!.play();
        })
        .catch((error) => {
          console.error("Błąd podczas uzyskiwania dostępu do kamery:", error);
          alert("Nie udało się uzyskać dostępu do tylnej kamery.");
        });
    }
  }, [capturing]);

  return (
    <div>
      <WhiteBoxWrapper>
        <h1 className="text-center">Dodaj wyposażenie</h1>
      </WhiteBoxWrapper>
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="mb-4">
          <SectionHeader>
            <label className="form-label">Kod QR</label>
          </SectionHeader>
          {qrCodeForm && <Alert variant="success">Zaczytany kod QR: {qrCodeForm}</Alert>}
          {!qrCodeForm && (
            <VideoWrapper>
              <video ref={videoRef} className="w-full border rounded mb-2" />
              <VideoScanBar />
            </VideoWrapper>
          )}
          {cameraError && <Alert variant="warning">{cameraError}</Alert>}
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        </div>

        <div className="mb-4">
          <SectionHeader>
            <label className="form-label" htmlFor="avatar">
              Zdjęcie
            </label>
          </SectionHeader>
          {previewImage && (
            <div className="mb-4">
              {captureMessage && <Alert variant="info">{captureMessage}</Alert>}
              <div className="border p-2 rounded previewImage">
                <img src={previewImage} alt="Podgląd zdjęcia" className="w-full rounded" />
              </div>
            </div>
          )}
          <Row className="align-items-center">
            <Col xs={6}>
              <input type="file" id="avatar" onChange={handleFileChange} className="form-control" accept="image/*" />
            </Col>
            <Col xs={6}>
              <button type="button" className="btn btn-secondary mt-2 w-100" onClick={() => setCapturing(true)}>
                Zrób zdjęcie
              </button>
            </Col>
          </Row>
        </div>

        {capturing && (
          <div className="mb-4">
            <VideoWrapper>
              <video ref={videoRefCamera} className="w-full max-w-full border rounded mb-2" />
              <canvas ref={canvasRef} style={{ display: "none" }} width={500} height={500}></canvas>
            </VideoWrapper>
            <button type="button" className="btn btn-success w-100" onClick={handleCapture}>
              {captureLoading ? "Zapisywanie..." : "Zapisz zdjęcie"}
            </button>
          </div>
        )}

        <div className="mb-4">
          <SectionHeader>
            <label className="form-label" htmlFor="name">
              Nazwa
            </label>
          </SectionHeader>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-control"
            required
          />
        </div>

        <div className="mb-4">
          <SectionHeader>
            <label className="form-label" htmlFor="category">
              Lokalizacja
            </label>
          </SectionHeader>
          <input
            type="text"
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="form-control"
            required
          />
        </div>

        <div className="mb-4">
          <SectionHeader>
            <label className="form-label" htmlFor="type">
              Rodzaj
            </label>
          </SectionHeader>
          <input
            type="text"
            id="type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="form-control"
            required
          />
        </div>

        <div className="mb-4">
          <SectionHeader>
            <label className="form-label" htmlFor="description">
              Opis
            </label>
          </SectionHeader>
          <input
            type="text"
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-control"
            required
          />
        </div>
        <SectionHeader>Parametry</SectionHeader>
        <div className="mb-4">
          <label className="form-label">
            <input type="checkbox" checked={returnable} onChange={(e) => setReturnable(e.target.checked)} />
            &nbsp; <b>Urządzenie stacjonarne lub urządzenie do pracy?</b>
          </label>
        </div>

        {isLoading && <p>Przetwarzanie kodu...</p>}

        <button
          type="button"
          onClick={handleSendData}
          disabled={isLoading}
          className={`btn btn-primary w-100 p-3 ${isLoading ? "bg-gray-400" : "bg-blue-600"}`}
        >
          {isLoading ? "Wysyłanie..." : "Dodaj wyposażenie +"}
        </button>
      </form>
    </div>
  );
};

export default AddDeviceForm;
